import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Plus, X, Briefcase, Search } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { directorsAPI, clientsAPI } from '@/lib/api';
import { toast } from 'sonner';
import logger from '@/lib/logger';
import DirectorFormDialog from './DirectorFormDialog';

/**
 * Client form section for managing director linkages.
 * Shows linked directors in a list and provides autocomplete to add new ones.
 * Note: relies on the parent component having already saved the client (clientId required).
 *       For NEW (unsaved) clients, this section is hidden until save.
 */
export default function ClientDirectorsSection({ clientId }) {
  const [linked, setLinked] = useState([]);
  const [allDirectors, setAllDirectors] = useState([]);
  const [search, setSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [adding, setAdding] = useState(null); // pending-add director, used for role/dates
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [linkRes, allRes] = await Promise.all([
        clientsAPI.listDirectors(clientId),
        directorsAPI.list(),
      ]);
      setLinked(linkRes.data || []);
      setAllDirectors(allRes.data || []);
    } catch (err) {
      logger.error('Load directors failed', err);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const linkedIds = useMemo(() => new Set(linked.map(l => l.id)), [linked]);
  const suggestions = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    return allDirectors
      .filter(d => !linkedIds.has(d.id))
      .filter(d => (d.name || '').toLowerCase().includes(q) || (d.din || '').includes(q.toUpperCase()))
      .slice(0, 8);
  }, [search, allDirectors, linkedIds]);

  const handlePickDirector = (director) => {
    setAdding({
      director_id: director.id,
      _name: director.name,
      _din: director.din,
      role: 'Director',
      date_of_appointment: '',
      date_of_cessation: '',
    });
    setSearch('');
    setShowSuggestions(false);
  };

  const handleConfirmAdd = async () => {
    if (!adding) return;
    try {
      await clientsAPI.linkDirector(clientId, {
        director_id: adding.director_id,
        role: adding.role,
        date_of_appointment: adding.date_of_appointment || null,
        date_of_cessation: adding.date_of_cessation || null,
      });
      toast.success('Director linked');
      setAdding(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link');
    }
  };

  const handleUnlink = async (director) => {
    if (!window.confirm(`Unlink ${director.name} (${director.din}) from this client?`)) return;
    try {
      await clientsAPI.unlinkDirector(clientId, director.id);
      toast.success('Director unlinked');
      load();
    } catch (err) {
      toast.error('Unlink failed');
    }
  };

  const handleDirectorCreated = () => {
    setCreateOpen(false);
    // Reload list and stay on autocomplete so user can pick the just-created one
    load();
    if (inputRef.current) inputRef.current.focus();
  };

  if (!clientId) {
    return (
      <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
        Save the client first to link directors / partners.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium flex items-center gap-2">
          <Briefcase className="h-3.5 w-3.5" /> Directors / Partners
          <Badge variant="outline" className="text-[10px] ml-1">{linked.length}</Badge>
        </Label>
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setCreateOpen(true)} data-testid="add-new-director-from-client">
          <Plus className="h-3 w-3 mr-1" /> Create new director
        </Button>
      </div>

      {/* Autocomplete */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={search}
          onChange={e => { setSearch(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          placeholder="Search director by name or DIN…"
          className="pl-9 h-9 text-sm"
          data-testid="director-search-input"
        />
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-30 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-md border bg-popover shadow-lg">
            {suggestions.map(d => (
              <button
                type="button"
                key={d.id}
                onMouseDown={() => handlePickDirector(d)}
                className="w-full text-left px-3 py-2 hover:bg-muted flex items-center justify-between gap-3"
                data-testid={`director-suggestion-${d.id}`}
              >
                <span className="text-sm font-medium truncate">{d.name}</span>
                <span className="text-[11px] text-muted-foreground font-mono">{d.din}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pending-add inline form */}
      {adding && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">{adding._name} <span className="text-[11px] text-muted-foreground font-mono ml-1">({adding._din})</span></div>
            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setAdding(null)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] text-muted-foreground">Role</Label>
              <Select value={adding.role} onValueChange={v => setAdding(a => ({ ...a, role: v }))}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Director">Director</SelectItem>
                  <SelectItem value="Managing Director">Managing Director</SelectItem>
                  <SelectItem value="Whole-Time Director">Whole-Time Director</SelectItem>
                  <SelectItem value="Independent Director">Independent Director</SelectItem>
                  <SelectItem value="Designated Partner">Designated Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Appointed</Label>
              <Input type="date" value={adding.date_of_appointment} onChange={e => setAdding(a => ({ ...a, date_of_appointment: e.target.value }))} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Cessation (optional)</Label>
              <Input type="date" value={adding.date_of_cessation} onChange={e => setAdding(a => ({ ...a, date_of_cessation: e.target.value }))} className="h-8 text-xs" />
            </div>
          </div>
          <div className="flex justify-end gap-1 pt-1">
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAdding(null)}>Cancel</Button>
            <Button type="button" size="sm" className="h-7 text-xs" onClick={handleConfirmAdd} data-testid="confirm-link-director">Add to Client</Button>
          </div>
        </div>
      )}

      {/* Linked list */}
      {loading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : linked.length === 0 ? (
        <p className="text-xs text-muted-foreground">No directors linked yet.</p>
      ) : (
        <div className="space-y-1.5">
          {linked.map(d => (
            <div key={d.id} className="flex items-center gap-3 rounded-md border bg-muted/20 p-2" data-testid={`linked-director-${d.id}`}>
              <Briefcase className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium truncate">{d.name}</span>
                  <span className="text-[10px] text-muted-foreground font-mono">{d.din}</span>
                  <Badge variant="secondary" className="text-[10px]">{d.role}</Badge>
                  {d.date_of_cessation && <Badge variant="outline" className="text-[10px] text-red-500 border-red-200">Ceased {d.date_of_cessation}</Badge>}
                </div>
                {d.date_of_appointment && <div className="text-[10px] text-muted-foreground">Appointed {d.date_of_appointment}</div>}
              </div>
              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500" onClick={() => handleUnlink(d)} data-testid={`unlink-director-${d.id}`}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <DirectorFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        director={null}
        onSaved={handleDirectorCreated}
      />
    </div>
  );
}
